/**
 * Shared API fetch utility that sends session cookies with every request.
 */

import { captureException as reporterCaptureException, captureMessage as reporterCaptureMessage, addBreadcrumb } from "@/lib/error-reporter"
import { noteRequestFailure, noteRequestSuccess } from "@/lib/net-journal"
import { notificationBus } from "./notification-bus"

// Strip query string from a path before crumbing — query strings can carry
// tokens, OTPs, and search terms we don't want loitering in error reports.
// Backend has its own scrubber for URLs that *do* slip through; this is the
// belt before that suspenders.
function _crumbPath(path: string): string {
  const q = path.indexOf("?")
  return q === -1 ? path : path.slice(0, q)
}

// Guard: once session expiry is detected and the redirect is scheduled,
// suppress further "Session Expired" dialogs from concurrent 401 responses.
// Reset when the page navigates away (the redirect itself clears this).
let _sessionExpiryHandled = false

// Guard: set to true by useLogout / self-service change-password just before
// they start their own async logout flow. Prevents `triggerSessionExpiry`
// from racing on WS `password_changed` frames or 403 SETTINGS_UPDATED
// responses that arrive during the handleLogout window (which would show a
// duplicate modal on top of the intentional logout). Module-scoped `let`
// (not sessionStorage) so it resets naturally on every page load — the OLD
// sessionStorage-based flag survived window.location.replace and F5, which
// left the tab permanently immune to session-expiry after any prior logout.
let _intentionalLogoutInProgress = false

/** Call from useLogout / self-service password-change flows right before the
 *  async logout starts. Prevents concurrent session-expiry modals during the
 *  ~few hundred ms of the logout flow. Auto-reset on next page load. */
export function markIntentionalLogoutInProgress() {
  _intentionalLogoutInProgress = true
}

// ─────────────────────────────────────────────────────────────────────────────
// Centralised session-termination flow. Every "session is over" path funnels
// here so we have exactly one place that:
//   1. Clears local auth state (localStorage user + perms)
//   2. Resolves the correct login URL based on stored role / current route
//   3. Fires the SESSION EXPIRED modal (notificationBus) with `nextPath` so
//      the LOGIN AGAIN button can navigate immediately
//   4. Schedules a 1.5 s backup redirect for users who dismiss the modal
//      (Escape, mobile gesture, browser back)
//   5. Dispatches the global `session-expired` CustomEvent so providers
//      (ws-provider, superadmin-layout) can stop background work
// Idempotent — multiple calls in the same page lifecycle no-op after the
// first.
// ─────────────────────────────────────────────────────────────────────────────
export function triggerSessionExpiry(opts?: {
  /** Telemetry / event-detail label for the trigger source. */
  reason?: string
  /** Override the modal title (default: "Session Expired"). */
  title?: string
  /** Override the modal body (default: "Please login again to continue."). */
  message?: string
}) {
  if (typeof window === "undefined") return
  if (_sessionExpiryHandled) return
  // Demo mode legitimately has no server-side session — don't show the dialog.
  if (
    sessionStorage.getItem("demo_mode") === "true" ||
    sessionStorage.getItem("admin_demo_mode") === "true"
  ) {
    return
  }
  // During active logout, the logout hook is handling the redirect itself.
  // Module-scoped flag — auto-resets on every page load, unlike the old
  // sessionStorage-based approach which would silently poison every future
  // triggerSessionExpiry after any prior logout in the same tab.
  if (_intentionalLogoutInProgress) return

  let storedRole: string | null = null
  try {
    const u = localStorage.getItem("user")
    if (u) storedRole = JSON.parse(u).role
  } catch {
    /* ignore parse errors */
  }

  localStorage.removeItem("user")
  localStorage.removeItem("perms")
  // Sweep per-user watchlist / tick caches on session-expiry to keep
  // localStorage tidy on shared devices. Keys are already user-scoped so
  // this is hygiene, not a security fix.
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i)
      if (k && (k.startsWith("watchlist_cache_") || k.startsWith("tick_cache_"))) {
        localStorage.removeItem(k)
      }
    }
  } catch { /* ignore */ }

  const isSuperAdminFlow =
    storedRole === "super_admin" || window.location.pathname.startsWith("/superadmin")
  const isTradeFlow = window.location.pathname.startsWith("/trade")
  const targetPath = isSuperAdminFlow
    ? "/superadmin/login"
    : isTradeFlow
      ? "/trade/login"
      : "/admin/login"

  const currentPath = window.location.pathname.replace(/\/$/, "")
  const onLoginAlready =
    currentPath === targetPath ||
    currentPath === "/admin/welcome" ||
    currentPath === "/admin/login" ||
    currentPath === "/superadmin/login" ||
    currentPath === "/trade/signup"
  if (onLoginAlready) return

  _sessionExpiryHandled = true

  // Trail entry must be added BEFORE dispatch — onSessionExpired() in the
  // reporter clears breadcrumbs as part of its handler, so anything pushed
  // after the event fires is lost.
  addBreadcrumb("auth", `session-expired (${opts?.reason || "api"}) → ${targetPath}`, {
    reason: opts?.reason || "api", target: targetPath,
  })

  // Notify external listeners (ws-provider, layouts) first so they can halt
  // background work (reconnect loops, polling) BEFORE the redirect fires.
  window.dispatchEvent(
    new CustomEvent("session-expired", {
      detail: { targetPath, source: opts?.reason || "api" },
    })
  )

  const title = opts?.title || "Session Expired"
  const message = opts?.message || "Please login again to continue."
  notificationBus.error(title, message, targetPath)
  setTimeout(() => {
    window.location.replace(targetPath)
  }, 2500)
}

// In-flight GET deduplication: concurrent identical GETs (same path + query)
// share a single promise so dashboard+hook+provider triple-fetches only hit
// the network once. Responses are cloned so each caller can read independently.
// Entries live only while the request is pending — no staleness risk.
const _inflightGets = new Map<string, Promise<Response>>()

function _inflightKey(path: string, body?: BodyInit | null) {
  // Only GET/HEAD are safe to dedup. Body is irrelevant for GET but included
  // for safety (won't be used).
  return `${path}|${body ? String(body) : ""}`
}

/**
 * v1-aware wrapper around `apiFetch`. When `NEXT_PUBLIC_USE_V1 === 'true'`,
 * any `/api/<x>` path is rewritten to `/api/v1/<x>`; otherwise the path is
 * passed through unchanged. Use this from hooks/components that should
 * follow the v1 rollout flag (e.g. useIdentity, usePortfolio). Existing
 * call sites that should keep hitting the legacy endpoint use `apiFetch`
 * directly.
 *
 * The flag is read at call time so flipping the env var (e.g. for a beta
 * deploy) takes effect without rebuilding the bundle. `apiFetch` itself
 * is untouched — flag-off behavior is byte-for-byte identical to the
 * legacy call.
 *
 * Audit 2026-05-13 / PR6.
 */
/**
 * Returns the trader-login POST path, flag-aware:
 *   * `/api/auth/trader-login` (legacy) when `NEXT_PUBLIC_USE_V1` is unset/false
 *   * `/api/v1/auth/login`     (v1)     when `NEXT_PUBLIC_USE_V1 === 'true'`
 *
 * The v1 endpoint was renamed (no `trader-` prefix — the surface implies the
 * role), so the generic `apiV1Fetch` rewrite can't apply. The trade-login
 * page uses plain `fetch` (no session yet → apiFetch's 401-handler would
 * misfire on bad passwords), so the URL choice has to be made at the call
 * site. This helper keeps the env check out of the React component. Audit
 * 2026-05-13 / PR15.
 */
export function getTraderLoginPath(): string {
  return process.env.NEXT_PUBLIC_USE_V1 === "true" ? "/api/v1/auth/login" : "/api/auth/trader-login"
}

export function apiV1Fetch(
  path: string,
  options: RequestInit & {
    skipErrorNotification?: boolean
    retries?: number
    retryDelay?: number
    timeoutMs?: number
  } = {}
): Promise<Response> {
  const useV1 = process.env.NEXT_PUBLIC_USE_V1 === "true"
  const shouldRewrite = useV1 && path.startsWith("/api/") && !path.startsWith("/api/v1/")
  const rewritten = shouldRewrite ? path.replace(/^\/api\//, "/api/v1/") : path
  return apiFetch(rewritten, options)
}

export async function apiFetch(
  path: string,
  options: RequestInit & {
    skipErrorNotification?: boolean
    retries?: number
    retryDelay?: number
    /**
     * Per-request timeout in ms applied via AbortSignal.timeout. Defaults to
     * 15 s. Bump for state-changing uploads (multipart FormData with an
     * attachment) — a 15 s default is hostile on HTTP/2 streams that are
     * competing with other in-flight requests, and a false-negative on an
     * approve/payment flow is far worse than a false-positive: the request
     * still lands at the server, but the client gives up and shows "Failed"
     * while the server happily commits the transaction.
     * Has no effect when the caller passes their own `signal`.
     */
    timeoutMs?: number
  } = {}
): Promise<Response> {
  const method = (options.method || "GET").toUpperCase()
  // Only dedup idempotent reads. Never dedup POST/PATCH/PUT/DELETE.
  if (method === "GET" || method === "HEAD") {
    const key = _inflightKey(path)
    const existing = _inflightGets.get(key)
    if (existing) {
      // Caller gets a cloned response so each reader can consume the body.
      return existing.then((r) => r.clone())
    }
    const promise = _doApiFetch(path, options).finally(() => {
      _inflightGets.delete(key)
    })
    _inflightGets.set(key, promise)
    return promise.then((r) => r.clone())
  }
  return _doApiFetch(path, options)
}

// Read the CSRF cookie value (non-httpOnly, set by the backend middleware).
// Returns undefined on the server (SSR) where document is unavailable, in
// which case we skip the header — server-side fetches don't target the
// CSRF-protected surface.
function _readCsrfToken(): string | undefined {
  if (typeof document === "undefined") return undefined
  const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]*)/)
  return match ? decodeURIComponent(match[1]) : undefined
}

// In-flight bootstrap, shared by every caller. A page load fires ~8-10
// requests at once; without this dedupe a cookieless boot would send that
// many parallel /api/csrf-token calls.
let _csrfBootstrap: Promise<string | undefined> | null = null

/**
 * Ask the backend for this session's CSRF token, which also re-syncs the
 * XSRF-TOKEN cookie if our copy drifted.
 *
 * Before this existed nothing ever called /api/csrf-token — the cookie was
 * only ever acquired as an incidental Set-Cookie on whichever request
 * happened to run first, so a client that lost or desynced it had no way
 * back. That is what turned the 2026-09-07 prod incident into twelve minutes
 * of hard 403s cured only by a full logout + login.
 */
function _fetchCsrfToken(): Promise<string | undefined> {
  if (typeof document === "undefined") return Promise.resolve(undefined)
  if (_csrfBootstrap) return _csrfBootstrap
  const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL || ""
  const url = backendUrl ? `${backendUrl.replace(/\/$/, "")}/api/csrf-token` : "/api/csrf-token"
  _csrfBootstrap = (async () => {
    try {
      const res = await fetch(url, {
        credentials: "include",
        signal: AbortSignal.timeout(10000),
      })
      const data = res.ok ? await res.json().catch(() => null) : null
      // Prefer the BODY. The backend verifies the header against the token it
      // derives from the session, so the endpoint's answer is authoritative —
      // whereas the cookie is only its transport, and is exactly what has
      // gone stale in the case this call exists to repair. Fall back to the
      // cookie when the body is unusable.
      return data?.csrfToken || _readCsrfToken()
    } catch {
      // Offline / timeout. Returning the current cookie (possibly undefined)
      // lets the caller proceed and fail normally rather than throwing from
      // a plumbing call the caller never asked for.
      return _readCsrfToken()
    } finally {
      _csrfBootstrap = null
    }
  })()
  return _csrfBootstrap
}

/** Cookie value if we have one, otherwise bootstrap it from the backend. */
async function _ensureCsrfToken(): Promise<string | undefined> {
  return _readCsrfToken() ?? (await _fetchCsrfToken())
}

// Generate a request_id the backend can use to stitch a single browser action
// across: API log entry → SQL → Redis publish → WS subscriber → frontend error log.
// Prefer crypto.randomUUID (modern browsers); fall back to a timestamp-seeded
// pseudo-UUID so the header is present even on older SSR/test environments.
function _newRequestId(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID()
    }
  } catch {
    /* ignore — fall through */
  }
  const hex = (n: number) =>
    Math.floor(Math.random() * 16 ** n)
      .toString(16)
      .padStart(n, "0")
  return `${Date.now().toString(16)}-${hex(4)}-${hex(4)}-${hex(4)}-${hex(12)}`
}

async function _doApiFetch(
  path: string,
  options: RequestInit & {
    skipErrorNotification?: boolean
    retries?: number
    retryDelay?: number
    timeoutMs?: number
  } = {}
): Promise<Response> {
  // Skip Content-Type for FormData — the browser must set multipart/form-data
  // with its own boundary string. Forcing application/json breaks file uploads.
  const isFormData = typeof FormData !== "undefined" && options.body instanceof FormData
  const requestId = _newRequestId()
  const method = (options.method || "GET").toUpperCase()

  // CSRF token: attach on state-changing requests so the backend can verify
  // it against the session. Safe methods don't need it and skipping keeps the
  // header clean. If the cookie isn't set yet (first request of a fresh
  // session, or a browser that dropped it), bootstrap it from
  // /api/csrf-token rather than firing a request we know will be rejected.
  const isSafeMethod = method === "GET" || method === "HEAD" || method === "OPTIONS"
  const csrfToken = isSafeMethod ? undefined : await _ensureCsrfToken()

  let headers: Record<string, string> = {
    ...(isFormData ? {} : { "Content-Type": "application/json" }),
    "X-Request-ID": requestId,
    ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    ...(options.headers as Record<string, string>),
  }

  const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL || ""
  const fullPath =
    path.startsWith("http") || !backendUrl
      ? path
      : `${backendUrl.replace(/\/$/, "")}/${path.replace(/^\//, "")}`

  const maxRetries = options.retries ?? 2
  const retryDelay = options.retryDelay ?? 500
  let attempt = 0
  // One-shot, separate from `attempt` — a CSRF re-sync is not a backoff
  // retry and must not consume the budget reserved for 5xx.
  let csrfRetried = false

  // Case 6 fix: ±25% jitter on retry delay. Without jitter, all clients
  // retry at the same wall-clock instant (t+500ms, t+1000ms) — on a server
  // restart this produces a thundering herd from every connected tab.
  // Jitter spreads retries across a 750ms-wide window per attempt.
  const jitteredDelay = (base: number) =>
    Math.max(0, Math.round(base * (0.75 + Math.random() * 0.5)))

  while (true) {
    const crumbPath = _crumbPath(path)
    const crumbStart = (typeof performance !== "undefined" ? performance.now() : Date.now())
    try {
      // F10 fix: per-request timeout to prevent hung connections. Default
      // 15 s; callers can override via `timeoutMs` for slow paths
      // (e.g. multipart uploads — see `timeoutMs` JSDoc on apiFetch options).
      const timeoutSignal = AbortSignal.timeout(options.timeoutMs ?? 15000)
      const res = await fetch(fullPath, {
        ...options,
        headers,
        credentials: "include", // Send session cookie with every request
        signal: options.signal || timeoutSignal,
      })

      // Crumb every completed HTTP call. Status + duration give 90% of the
      // post-mortem signal ("did the API even respond before the JS error?").
      // request_id ties this crumb back to the backend's app-logs entry for
      // the same call so Kibana can join the two with one filter.
      const ms = Math.round((typeof performance !== "undefined" ? performance.now() : Date.now()) - crumbStart)
      addBreadcrumb("http", `${method} ${crumbPath} → ${res.status}`, {
        method, path: crumbPath, status: res.status, ms, request_id: requestId,
      })

      // The server answered. This — not the `online` event, and not
      // `navigator.onLine` — is proof that connectivity is genuinely back, and
      // it is what closes an open outage and releases its journal to Kibana.
      // No-ops after a single localStorage read when no outage is in flight,
      // which is every request on a healthy connection.
      noteRequestSuccess({ path: crumbPath, ms })

      // NEW-1: Only retry idempotent methods. Never retry POST/PATCH — a 500 on an order
      // placement would create a duplicate if retried (client_nonce dedup is a last resort).
      // `method` is already in scope from the outer block.
      const isIdempotent = ["GET", "HEAD", "OPTIONS", "PUT", "DELETE"].includes(method)

      // Check if we should retry based on status code (5xx server error)
      if (
        !res.ok &&
        attempt < maxRetries &&
        isIdempotent &&
        [429, 500, 502, 503, 504].includes(res.status)
      ) {
        attempt++
        const delay = jitteredDelay(retryDelay * attempt)
        console.warn(
          `[apiFetch] Attempt ${attempt} failed with status ${res.status}. Retrying in ${delay}ms...`
        )
        await new Promise((resolve) => setTimeout(resolve, delay))
        continue
      }

      if (!res.ok) {
        // ── CSRF desync — re-sync and retry ONCE, before anything else.
        //
        // This is the one 403 that is safe to replay on a non-idempotent
        // method: `csrfMiddleware` is mounted ahead of every route in
        // app.js, so a CSRF_INVALID response proves no handler ran and
        // nothing was written. Checked before the session-expiry block
        // because a CSRF failure is not a session failure — surfacing it as
        // one would log the user out over a cookie hiccup.
        if (res.status === 403 && !csrfRetried && !isSafeMethod && typeof window !== "undefined") {
          const csrfClone = res.clone()
          const csrfData = await csrfClone.json().catch(() => ({}))
          if (csrfData?.code === "CSRF_INVALID") {
            csrfRetried = true
            const fresh = await _fetchCsrfToken()
            if (fresh && fresh !== headers["X-CSRF-Token"]) {
              headers = { ...headers, "X-CSRF-Token": fresh }
              addBreadcrumb("http", `${method} ${crumbPath} → CSRF re-sync, retrying`, {
                method,
                path: crumbPath,
                request_id: requestId,
              })
              continue
            }
          }
        }

        // ── Session expiry is ALWAYS handled, even when skipErrorNotification=true.
        // The watchlist tab never calls apiFetch, so the only trigger is the SSE probe
        // (which uses skipErrorNotification:true). Without this, 401 on SSE keepalive
        // silently stales the watchlist forever — the user never sees the "Session Expired" dialog.
        // Account blocked / settings updated — same end state as a 401, just
        // surfaced with a more specific title/message. Funnel through the
        // shared helper.
        if (res.status === 403 && typeof window !== "undefined" && !_sessionExpiryHandled) {
          const resClone = res.clone()
          const blockedData = await resClone.json().catch(() => ({}))
          if (blockedData.code === "ACCOUNT_BLOCKED" || blockedData.code === "SETTINGS_UPDATED") {
            const isSettingsUpdated = blockedData.code === "SETTINGS_UPDATED"
            triggerSessionExpiry({
              reason: isSettingsUpdated ? "settings-updated" : "account-blocked",
              title: isSettingsUpdated ? "Settings Updated" : "Account Blocked",
              message:
                blockedData.message ||
                (isSettingsUpdated
                  ? "Your account settings were updated. Please login again to continue."
                  : "Your account has been blocked."),
            })
            return res
          }
        }

        if (res.status === 401 && typeof window !== "undefined") {
          // Only treat a 401 as session-end when the backend explicitly says
          // so via `code: 'SESSION_EXPIRED'` (auth.middleware.js). Other 401s
          // are application errors (e.g. mistyped transaction password) that
          // controllers were historically returning incorrectly — those must
          // surface inline to the caller, not silently log the user out.
          // The frontend never sees an "unannotated 401" from a fixed
          // controller; if one slips through, we treat it as a normal error
          // (fall through to the !skipErrorNotification block below).
          const resClone = res.clone()
          const data = await resClone.json().catch(() => ({}))
          if (data?.code === "SESSION_EXPIRED" || data?.code === "NOT_AUTHENTICATED") {
            triggerSessionExpiry({ reason: "http-401" })
            return res
          }
        }

        // Parse the response body once — both the Kibana capture (always)
        // and the user-facing notification (conditional) need it. Empty /
        // non-JSON bodies fall back to {} so neither path throws on parse.
        let parsedData: { message?: string; error?: string } = {}
        if (res.status >= 500 || !options.skipErrorNotification) {
          const resClone = res.clone()
          parsedData = await resClone.json().catch(() => ({}))
        }
        const errorMessage = (parsedData.message ||
          parsedData.error ||
          `Request failed with status ${res.status}`) as string

        // Ship 5xx responses to the self-hosted error pipeline ALWAYS —
        // logging server-side errors is independent of whether we chose
        // to surface a toast to the user. Callers that pass
        // `skipErrorNotification:true` (e.g. forms with their own catch)
        // still need their 500s in Kibana for triage. request_id matches
        // the X-Request-Id header backend logs use, so the Telegram alert
        // and the backend's app-logs entry for the same failure correlate
        // 1:1 in Kibana.
        if (res.status >= 500) {
          reporterCaptureMessage(
            `API Error: ${options?.method || "GET"} ${path} → ${res.status} ${errorMessage}`,
            "error",
            { requestId, routeTag: "api_5xx" }
          )
        }

        if (!options.skipErrorNotification) {
          // Session-termination paths are now strictly code-based:
          //   - 401 with code SESSION_EXPIRED / NOT_AUTHENTICATED  → handled above
          //   - 403 with code ACCOUNT_BLOCKED / SETTINGS_UPDATED   → handled above
          // The previous substring matcher ("session expired" / "not logged in"
          // / "user not found") was removed because it logged users out when
          // unrelated lookups returned "User not found" or when controllers
          // happened to phrase non-auth errors with similar text.
          if (!_sessionExpiryHandled) {
            notificationBus.error("Error", errorMessage)
          }
        }
      }

      return res
    } catch (error: any) {
      // AbortError is intentional (caller cancelled the request) — never retry, never notify.
      if (error?.name === "AbortError") throw error

      const method = (options.method || "GET").toUpperCase()
      const isIdempotentCatch = ["GET", "HEAD", "OPTIONS", "PUT", "DELETE"].includes(method)
      if (attempt < maxRetries && isIdempotentCatch) {
        attempt++
        const delay = jitteredDelay(retryDelay * attempt)
        console.warn(
          `[apiFetch] Attempt ${attempt} failed with network error. Retrying in ${delay}ms...`
        )
        await new Promise((resolve) => setTimeout(resolve, delay))
        continue
      }

      // Crumb the terminal network failure (after retries exhausted) so the
      // trail records "we tried to call /api/orders and the network died"
      // rather than just the silent gap that's currently there.
      const netMs = Math.round((typeof performance !== "undefined" ? performance.now() : Date.now()) - crumbStart)
      addBreadcrumb("http", `${method} ${_crumbPath(path)} → network_error`, {
        method, path: _crumbPath(path), error: String(error?.name || "Error"), ms: netMs, request_id: requestId,
      })

      // Detect network errors up front — needed both for the capture-level
      // decision below AND the dialog-vs-toast branch further down.
      // request_id matches the X-Request-Id header backend logs use, so this
      // event correlates 1:1 with the backend's view of the failed request.
      const msg = error.message || "Failed to connect to server"
      const lowerMsg = msg.toLowerCase()
      // AbortSignal.timeout() throws a DOMException with name "TimeoutError"
      // and message "signal timed out" when the 15 s per-request cap fires.
      // In the wild this is the same UX as a Failed-to-fetch — cellular
      // warming up post phone-unlock, captive portal, backend momentarily
      // unreachable. Route it to the Connection Lost modal (which reloads
      // on Try Again) instead of the generic "Error / Close" modal which
      // just dismisses and leaves the trader on a blank screen. Component-
      // initiated aborts (name "AbortError" — portfolio fg/bg switch, tab
      // change) are intentional cancels and must NOT surface as a network
      // dialog, so we match on the timeout name specifically. Prod fingerprint
      // 8b448bcee536bdad: 192 hits over 14 d on /trade/dashboard, mobile
      // Android Chrome.
      const isAbortTimeout =
        (typeof DOMException !== "undefined" &&
          error instanceof DOMException &&
          error.name === "TimeoutError") ||
        lowerMsg.includes("signal timed out")
      const isNetworkError =
        typeof window !== "undefined" &&
        (error instanceof TypeError || lowerMsg.includes("failed to fetch") || isAbortTimeout)

      // Network errors are device-side conditions (flaky 4G, brief backend
      // hiccup, captive portal), not code bugs — log as `warn` so they
      // appear in Kibana for volume monitoring but don't trip ElastAlert
      // Rule 11 (which pages Telegram only on `level: error`). Real JS
      // exceptions still go through captureException → level: error.
      if (isNetworkError) {
        // Durable, synchronous, BEFORE any reporting is attempted. The report
        // below travels over the same dead path that just failed, so it may
        // never arrive; this write cannot fail for that reason. It is also the
        // only record that survives the "Try Again" reload, which is how the
        // entire sank09 outage came to be invisible.
        noteRequestFailure({
          path: crumbPath,
          method,
          errName: String(error?.name || "Error"),
          ms: netMs,
          requestId,
        })
        reporterCaptureMessage(`network_error: ${msg}`, "warn", {
          requestId,
          routeTag: "network_error",
          component: "api_network_error",
        })
      } else {
        reporterCaptureException(error, { requestId, routeTag: "network_error" })
      }

      if (!options.skipErrorNotification) {
        if (isNetworkError) {
          window.dispatchEvent(new Event("backend-connection-error"))
        } else {
          notificationBus.error("Error", msg)
        }
      }
      throw error
    }
  }
}
